import type { Permission } from '@customerio/hydra-permissions';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { OnboardingOnboardingAnswers } from '@customerio/hydra-ui-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IntegrationType } from '@/features/integrations';
import { isSystemTransactionalMessage, useSampleWorkspace } from '@/features/workspaces';
import type { Campaign } from '@/services/db/campaign/entity';
import { useCampaignsOptional } from '@/services/db/campaign/queries';
import { isStarterDomain, type Domain } from '@/services/db/domain/entity';
import { useDomainsOptional } from '@/services/db/domain/queries';
import type { PipelinesConnection } from '@/services/db/integration/entity';
import { usePipelinesConnectionsOptional } from '@/services/db/integration/queries';
import { getNewslettersQueryOptions } from '@/services/db/newsletter/queries';
import { useOnboardingAnswersOptional } from '@/services/db/onboarding-answers/queries';
import { getSegmentsQueryOptions, type SegmentsList } from '@/services/db/segment/queries';
import { getNonSampleSegments } from '@/services/db/segment/utils';
import type { IntegrationStatus } from '@/services/db/setup-guide/queries';
import { useIntegrationStatusOptional } from '@/services/db/setup-guide/queries';
import type { TransactionalMessage } from '@/services/db/transactional-message/entity';
import { getTransactionalMessagesQueryOptions } from '@/services/db/transactional-message/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { useIsAccountVerified } from './account-verification';

/**
 * The fixed 4-step essentials onboarding checklist. Completion is derived from
 * live state, never stored, and the flow only finishes via the Go live button.
 */
export const OnboardingStepId = {
  AccountVerified: 'account-verified',
  AddData: 'add-data',
  CreateWorkflow: 'create-workflow',
  SendingDomain: 'sending-domain',
} as const;
export type OnboardingStepId = (typeof OnboardingStepId)[keyof typeof OnboardingStepId];

export type OnboardingStepDefinition = {
  id: OnboardingStepId;
  title: string;
  /** Stage heading, when it differs from the rail label. */
  stageTitle?: string;
  /**
   * Revisitable by rail click or `?step=`. Non-navigable steps go on stage only
   * while blocking, and must stay a leading prefix of `ONBOARDING_STEP_ORDER`.
   */
  isNavigable: boolean;
};

export type OnboardingStepCompletion = {
  isComplete: boolean;
  isLoading: boolean;
  /**
   * The user cannot complete this step themselves (e.g. a missing permission).
   * It still goes on stage to explain itself, but doesn't lock the steps after it.
   */
  isBlocked: boolean;
  /**
   * The user chose to move past this step; overlaid from per-workspace storage.
   * Neither locks the steps after it nor lands on stage by default.
   */
  isSkipped: boolean;
};

export type OnboardingStepState = OnboardingStepDefinition & OnboardingStepCompletion;

export const ONBOARDING_STEP_ORDER: readonly OnboardingStepId[] = [
  OnboardingStepId.AccountVerified,
  OnboardingStepId.CreateWorkflow,
  OnboardingStepId.AddData,
  OnboardingStepId.SendingDomain,
];

/**
 * Titles are verb phrases from the design mock; don't shorten without designer
 * sign-off. The create-workflow title follows the wizard's `starting_point` answer.
 */
export const ONBOARDING_STEPS: Record<OnboardingStepId, OnboardingStepDefinition> = {
  [OnboardingStepId.AccountVerified]: {
    id: OnboardingStepId.AccountVerified,
    title: 'Account verified',
    // The stage is only reached while the account is NOT yet verified, so the
    // rail's past-tense win would read wrong as a heading there.
    stageTitle: 'Verify your account',
    isNavigable: false,
  },
  [OnboardingStepId.AddData]: {
    id: OnboardingStepId.AddData,
    title: 'Add your people or events',
    // The rail label names the outcome; the stage heading names the action.
    stageTitle: 'Connect your first data source',
    isNavigable: true,
  },
  [OnboardingStepId.CreateWorkflow]: {
    id: OnboardingStepId.CreateWorkflow,
    title: 'Create a workflow',
    isNavigable: true,
  },
  [OnboardingStepId.SendingDomain]: {
    id: OnboardingStepId.SendingDomain,
    title: 'Set up your sending domain',
    isNavigable: true,
  },
};

/** Which first asset the create-workflow step is about; unanswered fails open to workflows. */
export type StartingPointVariant = NonNullable<OnboardingOnboardingAnswers['starting_point']>;

export function getStartingPointVariant(
  startingPoint: OnboardingOnboardingAnswers['starting_point'] | undefined,
): StartingPointVariant {
  switch (startingPoint) {
    case 'workflows':
    case 'broadcasts':
    case 'transactional':
    case 'user_segmentation':
      return startingPoint;
    case null:
    case undefined:
      return 'workflows';
    default: {
      // The answers read is not schema-validated, so a value this union does
      // not know yet still has to land on the fallback at runtime.
      const _exhaustive: never = startingPoint;
      return 'workflows';
    }
  }
}

const CREATE_WORKFLOW_STEP_TITLES = {
  workflows: ONBOARDING_STEPS[OnboardingStepId.CreateWorkflow].title,
  broadcasts: 'Send your first broadcast',
  transactional: 'Send a transactional message',
  user_segmentation: 'Segment your audience',
} as const satisfies Record<StartingPointVariant, string>;

export function getCreateWorkflowStepTitle(
  startingPoint: OnboardingOnboardingAnswers['starting_point'] | undefined,
): string {
  return CREATE_WORKFLOW_STEP_TITLES[getStartingPointVariant(startingPoint)];
}

type HasPermission = (permission: Permission) => boolean;

/**
 * Create and view permission for the variant's asset (view feeds completion).
 * Broadcasts accept either the newsletter or the API-triggered pair, never a mix.
 */
export function canUseStartingPoint(hasPermission: HasPermission, variant: StartingPointVariant): boolean {
  const canCreateAndView = (create: Permission, view: Permission) => hasPermission(create) && hasPermission(view);
  switch (variant) {
    case 'workflows':
      return canCreateAndView('journeys/campaigns/create', 'journeys/campaigns/view');
    case 'broadcasts':
      return (
        canCreateAndView('journeys/newsletters/create', 'journeys/newsletters/view') ||
        canCreateAndView('journeys/broadcasts/create', 'journeys/broadcasts/view')
      );
    case 'transactional':
      return canCreateAndView('journeys/transactional_messages/create', 'journeys/transactional_messages/view');
    case 'user_segmentation':
      return canCreateAndView('journeys/segments/create', 'journeys/segments/view');
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

/** Earliest step that can go on stage; `max` guards the all-non-navigable case. */
const MIN_NAVIGABLE_STEP_INDEX = Math.max(
  0,
  ONBOARDING_STEP_ORDER.findIndex((id) => ONBOARDING_STEPS[id].isNavigable),
);

function useAccountVerifiedStepStatus(): OnboardingStepCompletion {
  // While unverified, this step blocks the checklist: it goes on stage and
  // every other step is locked (reachability collapses to index 0).
  const isComplete = useIsAccountVerified();
  return { isComplete, isLoading: false, isBlocked: false, isSkipped: false };
}

/** Done once the workspace has received any data: a person or an event. */
export function isAddDataComplete(status: Pick<IntegrationStatus, 'customers' | 'events'> | undefined): boolean {
  return (status?.customers ?? 0) > 0 || (status?.events ?? 0) > 0;
}

type ConnectionLike = Pick<PipelinesConnection, 'direction' | 'is_auto_track' | 'last_activity'>;

/**
 * A user-added Pipelines source counts once it has received data. The wizard
 * creates the row on mount, so a bare row (abandoned setup) must not count.
 */
export function hasUserAddedSource(connections: readonly ConnectionLike[] | undefined): boolean {
  return (connections ?? []).some(
    (connection) =>
      connection.direction === IntegrationType.pipelinesSource &&
      !connection.is_auto_track &&
      connection.last_activity > 0,
  );
}

function useAddDataStepStatus(): OnboardingStepCompletion {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const integrationStatus = useIntegrationStatusOptional({ workspaceId });
  const connections = usePipelinesConnectionsOptional({ workspaceId });
  // Fail closed: a failed read just drops its half of the signal.
  const isComplete = isAddDataComplete(integrationStatus.data) || hasUserAddedSource(connections.data);
  return {
    isComplete,
    // One positive signal settles the step; don't hold on the slower request.
    // `isLoading`, not `isPending`: a disabled query must not read as loading.
    isLoading: !isComplete && (integrationStatus.isLoading || connections.isLoading),
    isBlocked: false,
    isSkipped: false,
  };
}

type CampaignLike = Pick<Campaign, 'deleted'>;

/**
 * Done once any non-deleted campaign exists. Template-created campaigns carry a
 * legacy `sample: true` stamp and must count, so there is no sample exclusion.
 */
export function hasCreatedWorkflow(campaigns: readonly CampaignLike[] | undefined): boolean {
  return (campaigns ?? []).some((campaign) => !campaign.deleted);
}

/** Done once a newsletter or a non-deleted API-triggered broadcast exists. */
export function hasCreatedBroadcast(
  newsletters: readonly unknown[] | undefined,
  campaigns: readonly Pick<Campaign, 'deleted' | 'type'>[] | undefined,
): boolean {
  return (
    (newsletters ?? []).length > 0 ||
    (campaigns ?? []).some((campaign) => campaign.type === 'triggered_broadcast' && !campaign.deleted)
  );
}

/**
 * Done once a customer-created message exists. The provisioned `is_default`
 * catch-all has an empty trigger name, so it must be excluded explicitly.
 */
export function hasCreatedTransactionalMessage(
  messages: readonly Pick<TransactionalMessage, 'trigger_name' | 'is_default'>[] | undefined,
): boolean {
  return (messages ?? []).some((message) => !isSystemTransactionalMessage(message) && message.is_default !== true);
}

/** Done once a segment beyond the seeded "Sample"-tagged ones exists. */
export function hasCreatedSegment(segments: SegmentsList | undefined): boolean {
  return segments != null && getNonSampleSegments(segments).length > 0;
}

type VariantAssetSources = {
  campaigns: readonly Pick<Campaign, 'deleted' | 'type'>[] | undefined;
  newsletters: readonly unknown[] | undefined;
  transactionalMessages: readonly Pick<TransactionalMessage, 'trigger_name' | 'is_default'>[] | undefined;
  segments: SegmentsList | undefined;
};

function hasVariantAsset(variant: StartingPointVariant, sources: VariantAssetSources): boolean {
  switch (variant) {
    case 'workflows':
      return hasCreatedWorkflow(sources.campaigns);
    case 'broadcasts':
      return hasCreatedBroadcast(sources.newsletters, sources.campaigns);
    case 'transactional':
      return hasCreatedTransactionalMessage(sources.transactionalMessages);
    case 'user_segmentation':
      return hasCreatedSegment(sources.segments);
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

/**
 * The variant's list reads, each enabled only for its variant and view permission.
 * Cached rows from disabled observers are masked before the predicate sees them.
 */
type VariantAssetReads = { hasAsset: boolean; isLoading: boolean };

/** Which list reads the variant needs, given the variant's view permissions. */
type VariantReadFlags = {
  campaigns: boolean;
  newsletters: boolean;
  transactionalMessages: boolean;
  segments: boolean;
};

const NO_READS: VariantReadFlags = {
  campaigns: false,
  newsletters: false,
  transactionalMessages: false,
  segments: false,
};

/** Nothing fires until the variant is known, and each read needs its own view permission. */
export function getVariantReadFlags(
  variant: StartingPointVariant,
  isVariantPending: boolean,
  hasPermission: HasPermission,
): VariantReadFlags {
  if (isVariantPending) {
    return NO_READS;
  }
  switch (variant) {
    case 'workflows':
      return { ...NO_READS, campaigns: hasPermission('journeys/campaigns/view') };
    case 'broadcasts':
      return {
        ...NO_READS,
        campaigns: hasPermission('journeys/broadcasts/view'),
        newsletters: hasPermission('journeys/newsletters/view'),
      };
    case 'transactional':
      return { ...NO_READS, transactionalMessages: hasPermission('journeys/transactional_messages/view') };
    case 'user_segmentation':
      return { ...NO_READS, segments: hasPermission('journeys/segments/view') };
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
}

/**
 * The four list reads, always mounted (fixed hook order), each enabled by its flag.
 * staleTime 0: the asset is created away from this screen, so a remount must refetch.
 */
function useVariantAssetQueries(variant: StartingPointVariant, reads: VariantReadFlags, workspaceId: number | null) {
  const queryClient = useQueryClient();
  const hasWorkspace = workspaceId != null && workspaceId > 0;
  const keyWorkspaceId = workspaceId ?? 0;
  const campaigns = useCampaignsOptional({
    workspaceId: reads.campaigns ? workspaceId : null,
    // Server-filtered so an API-triggered broadcast past page 1 of the
    // unfiltered list can't be missed.
    params: variant === 'broadcasts' ? { page: 1, type: 'triggered_broadcast' } : { page: 1 },
    queryConfig: { staleTime: 0 },
  });
  const newsletters = useQuery({
    ...getNewslettersQueryOptions(keyWorkspaceId, queryClient),
    staleTime: 0,
    enabled: reads.newsletters && hasWorkspace,
  });
  const transactionalMessages = useQuery({
    ...getTransactionalMessagesQueryOptions(keyWorkspaceId, queryClient),
    staleTime: 0,
    enabled: reads.transactionalMessages && hasWorkspace,
  });
  const segments = useQuery({
    ...getSegmentsQueryOptions(keyWorkspaceId, queryClient),
    staleTime: 0,
    enabled: reads.segments && hasWorkspace,
  });
  return { campaigns, newsletters, transactionalMessages, segments };
}

/** A disabled observer can still surface cached rows; only an enabled read's data may count. */
const readIf = <T>(enabled: boolean, data: T | undefined): T | undefined => (enabled ? data : undefined);

function useVariantAssetReads(
  variant: StartingPointVariant,
  isVariantPending: boolean,
  workspaceId: number | null,
  hasPermission: HasPermission,
): VariantAssetReads {
  const reads = getVariantReadFlags(variant, isVariantPending, hasPermission);
  const queries = useVariantAssetQueries(variant, reads, workspaceId);
  return {
    hasAsset: hasVariantAsset(variant, {
      campaigns: readIf(reads.campaigns, queries.campaigns.data?.campaigns),
      newsletters: readIf(reads.newsletters, queries.newsletters.data?.newsletters),
      transactionalMessages: readIf(
        reads.transactionalMessages,
        queries.transactionalMessages.data?.transactionalMessages,
      ),
      segments: readIf(reads.segments, queries.segments.data),
    }),
    // `isLoading`, not `isPending`: a disabled read must not hold the screen.
    isLoading: Object.values(queries).some((query) => query.isLoading),
  };
}

function useCreateWorkflowStepStatus(
  variant: StartingPointVariant,
  isVariantPending: boolean,
): OnboardingStepCompletion {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const { hasPermission } = usePermissions();
  // The injected read-only sample workspace's assets must never count.
  const {
    isSampleWorkspace,
    isLoading: isSampleWorkspaceLoading,
    isError: isSampleWorkspaceError,
  } = useSampleWorkspace();
  const reads = useVariantAssetReads(variant, isVariantPending, workspaceId, hasPermission);
  // Unknown sampleness (pending or failed lookup) means `isSampleWorkspace`
  // answers false for everything — treat both like loading so the sample
  // workspace's assets can't complete the step through a failed read.
  const isSampleWorkspaceUnknown = isSampleWorkspaceLoading || isSampleWorkspaceError;
  // Fail closed: a failed read reports incomplete. Pending answers hold the
  // step, or the fallback variant would flash on stage and then jump.
  const isComplete =
    !isVariantPending && reads.hasAsset && !isSampleWorkspaceUnknown && !isSampleWorkspace(workspaceId);
  return {
    isComplete,
    // The sample lookup only holds when it is the deciding factor: a
    // settled-empty read is incomplete regardless of sampleness.
    isLoading: isVariantPending || (!isComplete && (reads.isLoading || (reads.hasAsset && isSampleWorkspaceLoading))),
    // A member without the variant's permissions can land here. Blocked, not
    // stuck: the step explains itself while the rest stays reachable.
    isBlocked: !canUseStartingPoint(hasPermission, variant),
    isSkipped: false,
  };
}

/** Done once a customer-added domain is verified; provisioned starter domains never count. */
export function hasVerifiedCustomDomain(
  domains: readonly Pick<Domain, 'domain' | 'verified' | 'starter'>[] | undefined,
): boolean {
  return (domains ?? []).some((d) => d.verified === true && !isStarterDomain(d));
}

function useSendingDomainStepStatus(): OnboardingStepCompletion {
  // Plain read: the mounted step polls this key while a domain is pending, and
  // rechecks refresh it on Entri close and on each step visit. Completing this
  // step must not auto-finish the flow.
  const domains = useDomainsOptional();
  const isComplete = hasVerifiedCustomDomain(domains.data?.domains);
  return { isComplete, isLoading: !isComplete && domains.isLoading, isBlocked: false, isSkipped: false };
}

/** Live state of every step, in rail order. One status hook per step, in fixed call order. */
export function useOnboardingSteps(): OnboardingStepState[] {
  // The wizard's answers pick the create-workflow variant. Readers fail open
  // to workflows on a settled error or an unanswered wizard.
  const answers = useOnboardingAnswersOptional();
  const startingPoint = answers.data?.answers?.starting_point;
  const completionById: Record<OnboardingStepId, OnboardingStepCompletion> = {
    [OnboardingStepId.AccountVerified]: useAccountVerifiedStepStatus(),
    [OnboardingStepId.AddData]: useAddDataStepStatus(),
    [OnboardingStepId.CreateWorkflow]: useCreateWorkflowStepStatus(
      getStartingPointVariant(startingPoint),
      answers.isLoading,
    ),
    [OnboardingStepId.SendingDomain]: useSendingDomainStepStatus(),
  };
  return ONBOARDING_STEP_ORDER.map((id) => {
    const definition =
      id === OnboardingStepId.CreateWorkflow
        ? { ...ONBOARDING_STEPS[id], title: getCreateWorkflowStepTitle(startingPoint) }
        : ONBOARDING_STEPS[id];
    return { ...definition, ...completionById[id] };
  });
}

/** An incomplete step the user can and should act on: neither blocked nor skipped. */
const isActionableIncomplete = (step: OnboardingStepCompletion): boolean =>
  !step.isComplete && !step.isBlocked && !step.isSkipped;

/** What reachability needs: completion, plus whether the step can be opened at all. */
export type ReachabilityStep = OnboardingStepCompletion & Pick<OnboardingStepDefinition, 'isNavigable'>;

/**
 * True while the step that would go on stage is unknown. A blocked or skipped
 * step is passed over only once settled, or a complete one would flash then jump.
 */
export function isBlockingStepLoading(steps: readonly OnboardingStepCompletion[]): boolean {
  const first = steps.find((step) => step.isLoading || isActionableIncomplete(step));
  return first?.isLoading ?? false;
}

/**
 * True once no step is still reading its status. `isBlockingStepLoading` only
 * covers the step that goes on stage; reports that summarize every step wait on this.
 */
export function areAllStepsSettled(steps: readonly OnboardingStepCompletion[]): boolean {
  return steps.every((step) => !step.isLoading);
}

/**
 * The step to work on: the first incomplete one, blocked included, skipped
 * excluded. When nothing remains the last step stays current (Go live ends the flow).
 */
export function getFirstIncompleteIndex(steps: readonly OnboardingStepCompletion[]): number {
  if (steps.length === 0) {
    return 0;
  }
  const index = steps.findIndex((step) => !step.isComplete && !step.isSkipped);
  return index === -1 ? steps.length - 1 : index;
}

/**
 * Furthest step the rail lets the user open: the first actionable incomplete
 * step or the last completed one, whichever is later. Blocked and skipped steps
 * are passed over; when nothing actionable remains the whole rail is reachable.
 */
export function getMaxReachableIndex(steps: readonly ReachabilityStep[]): number {
  if (steps.length === 0) {
    return 0;
  }
  const frontier = steps.findIndex(isActionableIncomplete);
  if (frontier === -1) {
    return steps.length - 1;
  }
  // A non-navigable frontier is a gate, not a task: it locks everything after
  // it, so a later completed step must not raise the ceiling past it. Steps
  // complete on derived state, so one can be done while the gate is shut.
  if (!steps[frontier].isNavigable) {
    return frontier;
  }
  const lastCompleted = steps.findLastIndex((step) => step.isComplete);
  return Math.max(frontier, lastCompleted);
}

export function isOnboardingStepId(value: string | null | undefined): value is OnboardingStepId {
  return ONBOARDING_STEP_ORDER.some((id) => id === value);
}

/**
 * Forward jumps clamp to the last reachable step, back-navigation to the first
 * navigable one. When a non-navigable step is the blocker, the floor drops to it.
 */
export function clampStepIndex(requestedIndex: number, maxReachableIndex: number): number {
  const minIndex = Math.min(MIN_NAVIGABLE_STEP_INDEX, maxReachableIndex);
  return Math.max(minIndex, Math.min(requestedIndex, maxReachableIndex));
}
